/*
 * IGNIERE §162 検索パネル（2026-08-08 新設）
 * 設計図＝60_開発/shopify/作業/search_2026-08-08/mapping.md
 *
 * やること
 *   1. パネルが開いたら /search?q=*&view=facets から軸・値・件数を取って描く
 *   2. 値を選ぶたびに取り直し、件数を更新し、0 件の値を選べなくする（裁定 #3 #4）
 *   3. 送信は 1 か所（buildUrl）に集約する。キーワードが空なら q=* を補う（裁定 #2）
 *
 * 触っていないもの
 *   - 純正の predictive-search-component（キーワード欄と入力補助）
 *   - dialog の開閉（assets/dialog.js）
 */

(() => {
  'use strict';

  const ROOT = '[data-ig-search-panel]';
  const DEBOUNCE_MS = 250;
  const WILDCARD = '*';
  /* 2026-10-03（代表裁定）CATEGORY は大分類だけ。値は記号（we・hl…）で、表示名と並びと棚の handle は
     対応表（snippets/igniere-category-groups.liquid）から引く。
     正本は基幹の分類表（大分類表・細分類表）・生成器 build_category_groups.py＝ここで直書きしない。 */
  const MAJOR = 'filter.p.m.custom.major_category';
  const TYPE = 'filter.p.product_type';

  /** §179-8（2026-08-13 user 裁定）軸見出しは英語＝S&D の設定ラベルをそのまま使う。
      旧 AXIS_LABEL_JA（和名への寄せ・§162）は S&D 英語裁定（8/12）と衝突し撤回。
      空の対応表として残す＝無い軸は元のラベルのまま、の仕組みは不変 */
  const AXIS_LABEL_JA = {};
  /* §280-10（2026-10-07 代表「「英語/日本語」の併記が必要かも。英語だけだと直感的ではない」）英語の見出しは残し、
     日本語を従として添える（SP メニューの札 §16.30 と同じ文法）。写し元＝snippets/igniere-axis-ja.liquid */
  const AXIS_SUB_JA = { CATEGORY: 'カテゴリー', 'ITEM TYPE': '商品の種類', SCENE: 'シーン', BRAND: 'ブランド', PRICE: '価格' };

  /* §163-17（Codex 指摘 L1・2026-08-08）`.jp` の付与をやめる。
     当てていた CSS（和文だけ 0.93 倍）は §162-49 で全廃済みで、**当てる先が無い**。
     クラスだけ残すと「まだ補正が生きている」と読める。書体側の size-adjust（§14.7）
     が全体で面倒を見るので、こちらで和文を見分ける必要はない。 */

  const yen = (cents) => '¥' + Math.round(Number(cents || 0) / 100).toLocaleString('ja-JP');

  class SearchPanel {
    constructor(el) {
      this.el = el;
      this.searchUrl = el.dataset.facetsUrl || '/search';
      this.allUrl = el.dataset.allUrl || '/collections/all';
      this.collectionsUrl = (el.dataset.collectionsUrl || '/collections').replace(/\/+$/, '');
      /* 実在する大分類の棚（handle→商品数）。Liquid が custom.category_level＝大分類 の棚から作る */
      try {
        const mc = JSON.parse(el.dataset.igMajorCollections || '{}');
        this.majorCols = mc && typeof mc === 'object' && !Array.isArray(mc) ? mc : {};
      } catch (_) {
        this.majorCols = {};
      }
      this.axesToShow = (el.dataset.axes || '').split(',').map((s) => s.trim()).filter(Boolean);

      this.axesEl = el.querySelector('[data-ig-sp-axes]');
      this.chipsEl = el.querySelector('[data-ig-sp-chips]');
      /* ⚠ §162-16 で送信ボタンを .ig-sp の**外**（フォーム末尾）へ移した。
         el（=.ig-sp）から探すと null になり、件数が反映されず押しても何も起きない。
         実際にこの取りこぼしを検証で捕まえた（ラベルが初期値「商品をさがす」のまま）。
         → 器（dialog）から探す。 */
      const scope = el.closest('dialog') || document;
      this.submitEl = scope.querySelector('[data-ig-sp-submit]');
      this.submitLabelEl = scope.querySelector('[data-ig-sp-submit-label]');
      /* §179-9：クリアは足元（送るボタンの左）へ移設＝.ig-sp の外なので器から探す（§162-16 と同じ罠） */
      this.clearEl = scope.querySelector('[data-ig-sp-clear]');
      this.loadingEl = el.querySelector('[data-ig-sp-loading]');
      this.statusEl = el.querySelector('[data-ig-sp-status]');
      this.picksEl = el.querySelector('[data-ig-sp-picks]');
      this.picksSeq = 0;

      /** 選択状態：Map<param, Set<value>>。価格だけは {gte, lte} を持つ */
      this.selected = new Map();
      this.price = { gte: null, lte: null };
      /* §280-9（2026-10-07 代表の裁定）虫眼鏡で開く検索は毎回まっさらで始める＝今いる URL の条件は戻さない（下の dialog:open）。 */

      this.lastFacets = null;
      this.reqSeq = 0;
      this.timer = null;
      this.loadedOnce = false;
      this.pending = null;

      this.dialog = el.closest('dialog');
      this.input = el.closest('dialog')?.querySelector('input[type="search"][name="q"]') || null;
      this.form = el.closest('dialog')?.querySelector('form.predictive-search-form') || null;

      this.bind();

      /* §162-55（user 報告 2026-08-08）「一覧から開き直すと、語もクリアも消える」。
         原因＝**復元は器の組み立て時に 1 回しか走らない**。純正は器を閉じる時に
         入力欄を空にするので、2 回目に開くと空のままだった（1 回目は残る＝
         「1 度目は出るのに 2 度目は消える」という分かりにくい形で出ていた）。
         → 器が開くたびに復元し直す（純正の `dialog:open` を拾う）。 */
      /* §162-56（user 実機報告 2026-08-08）**1 回目だけ語が入らない**。
         真因＝純正 predictive-search は器を初めて開いた時だけ（`once: true`）
         「最近見た商品」の初期表示を読み込み、その中で**入力欄を空にする**。
         こちらの復元がその前に走るため、直後に消されていた。2 回目以降は
         この初回処理が二度と走らないので、復元がそのまま残る＝
         「1 回目だけ消える」という分かりにくい形になっていた。
         **「最近見た商品」を持っている人にしか起きない**ため、まっさらな検証環境
         （自動テスト）では一度も再現しなかった（user のスマホ実機で発覚）。
         → 開いた後、純正の初回処理が終わる番でもう一度当て直す。 */
      /* §280-9（2026-10-07 代表の実機の指摘「開き直した時に、今フィルター（検索）が効いていることがわかりづらい」→
         代表の裁定「虫眼鏡で開く検索は、毎回まっさらで始める」）。
         旧 §162-36／§162-55／§162-56 は「開くたびに今いる URL の条件と語を戻す」作りだった。
         戻すと、結果を見ている人が虫眼鏡を開いた時に「前の条件がまだ効いている」ことに気づかず、
         その上に条件を足して 0 件になる（代表の実機）。検索の窓は「新しく探す入口」、今の結果の中を狭めるのは
         一覧の FILTER（一覧の絞り込みは「今居る一覧の中を狭める道具」＝2026-10-04 代表の裁定と同じ役割の分け方）。
         → 開くたびに選択・価格・語を空にする。純正は閉じる時に語を空にし、初めて開いた時にも空にするので向きが同じ＝
         旧 §162-56 の「後から当て直す」（rAF 2 回・300ms 後）は要らない（遅れて空にすると、開いてすぐ選んだ物まで消える）。 */
      const dlg = this.dialog?.closest('dialog-component') || this.dialog;
      dlg?.addEventListener('dialog:open', () => {
        /* §179-15：この開扉サイクルの「最初の描画」に時差入場を付ける準備。
           既に描画済み（先読み・前回の開扉）なら、今ある子にその場で発火 */
        this._enterPending = true;
        if (this.axesEl && this.axesEl.children.length) this.runEnterStagger();
        this.resetForOpen();
      });
    }

    /* §280-9：開くたびにまっさら（選択・価格・語）。描き直しは件数の取り直し（schedule）に任せる */
    resetForOpen() {
      this.selected.clear();
      this.price = { gte: null, lte: null };
      if (this.input && this.input.value) {
        this.input.value = '';
        this.dialog?.querySelector('.predictive-search__reset-button')?.setAttribute('hidden', '');
      }
      this.syncMode();
      this.renderChips();
      this.schedule();
    }

    /* ---------- 送信先の組み立て（ここ 1 か所に集約する） ---------- */
    buildUrl(extra) {
      const params = new URLSearchParams();
      const typed = (this.input?.value || '').trim();
      // 裁定 #2：キーワードが空なら q=* を補う（ユーザーには見せない）
      params.set('q', typed || WILDCARD);

      for (const [param, values] of this.selected) {
        for (const v of values) params.append(param, v);
      }
      if (this.price.gte != null) params.set('filter.v.price.gte', String(this.price.gte));
      if (this.price.lte != null) params.set('filter.v.price.lte', String(this.price.lte));

      if (extra) for (const [k, v] of Object.entries(extra)) params.set(k, v);
      return `${this.searchUrl}?${params.toString()}`;
    }

    facetsUrl() {
      // 商品の軸だけが要るので type=product を固定する
      return this.buildUrl({ view: 'facets', type: 'product' });
    }

    /* §162-54（user 指摘 2026-08-08）「語で絞ると軸が消える」への根治。

       仕組みの事実（実測）：
         - **絞り込みで狭めた時**は、Shopify は 0 件の値も返す（暗転で残せる＝裁定 #4 どおり）
         - **語で狭めた時**は、0 件の値を**そもそも返さない**（ブランド 5 → 2 に減る）
       同じ「狭める」なのに語の時だけ値が消え、「掛け合わせができなくなった」ようにも見えていた。

       対策：**語も絞り込みも付けない全体像**（台帳）を器を開くたびに 1 回だけ取り、
       絞った結果に無い値は **0 件として補って暗転で残す**。これで語でも絞り込みでも
       同じ見え方になる。 */
    masterUrl() {
      return `${this.searchUrl}?q=${encodeURIComponent(WILDCARD)}&view=facets&type=product`;
    }

    fetchMaster() {
      if (this.masterPromise) return this.masterPromise;
      this.masterPromise = fetch(this.masterUrl(), { headers: { Accept: 'application/json' } })
        /* §163-27（Codex 再指摘）404/500 も「失敗」として扱う。
           以前は r.ok が false でも約束が成功で終わっていたため下の catch に落ちず、
           masterPromise を握ったままになって、そのページでは二度と取り直せなかった。 */
        .then((r) => {
          if (!r.ok) throw new Error('master ' + r.status);
          return r.json();
        })
        .then((d) => {
          this.master = d && Array.isArray(d.filters) ? d.filters : null;
          return this.master;
        })
        .catch(() => {
          // 取れなくても機能は落とさない（従来どおり＝返ってきた値だけを出す）
          this.master = null;
          /* §163-11（Codex 指摘 M3）**失敗した約束を持ち続けない**。
             握ったままだと、そのページでは二度と取りに行かず、通信が
             一瞬こけただけで「軸が消えたまま」が最後まで直らない。
             捨てておけば、次に軸をいじった時に取り直せる。 */
          this.masterPromise = null;
          return null;
        });
      return this.masterPromise;
    }

    /** 台帳に在って応答に無い値を、0 件として足す（並び順は台帳を正とする） */
    mergeWithMaster(data) {
      if (!this.master || !data || !Array.isArray(data.filters)) return data;
      const byParam = new Map(this.master.map((f) => [f.param, f]));
      data.filters = data.filters.map((f) => {
        const m = byParam.get(f.param);
        if (!m || !Array.isArray(m.values) || !Array.isArray(f.values)) return f;
        const got = new Map(f.values.map((v) => [v.v, v]));
        const merged = m.values.map((mv) => got.get(mv.v) || { v: mv.v, l: mv.l, c: 0, a: false });
        // 台帳より後に増えた値（台帳取得後に商品が増えた等）も落とさない
        for (const v of f.values) if (!m.values.some((mv) => mv.v === v.v)) merged.push(v);
        return Object.assign({}, f, { values: merged });
      });
      return data;
    }

    /* ---------- 先読み ----------
     * 開いてから取りに行くと、空の器（273px）で約 700ms 待たされ、
     * データが届いた瞬間に 626px へ一段跳ねる（2026-08-08 実測・1 コマ刻みで確認）。
     * 2 回目以降が滑らかなのは、既に持っているから。
     * ＝**開く前に取っておけば、1 回目から 2 回目と同じ動きになる**。
     * 引き金は 2 つ：①虫眼鏡に触れた／焦点が当たった時（意図が見えた瞬間）
     *              ②ページが落ち着いた後の暇な時間（保険）
     */
    /* §162-36⑥（Codex 指摘）通信の前に「取得済み」を立てていたため、1 度失敗すると
       ページを読み直すまで二度と取りに行けなかった。**成功した時だけ取得済みにする**。 */
    prefetch() {
      if (this.loadedOnce || this.pending) return;
      this.pending = this.fetchFacets()
        .then((ok) => {
          if (ok !== false) this.loadedOnce = true;
        })
        .finally(() => {
          this.pending = null;
        });
    }

    armPrefetch() {
      const btns = document.querySelectorAll('search-button button');
      const go = () => this.prefetch();
      btns.forEach((b) => {
        b.addEventListener('pointerenter', go, { once: true });
        b.addEventListener('focus', go, { once: true });
        b.addEventListener('touchstart', go, { once: true, passive: true });
      });
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
      idle(() => this.prefetch(), { timeout: 3000 });
    }

    /* ---------- 取得 ---------- */
    fetchFacets() {
      const seq = ++this.reqSeq;
      this.el.classList.add('is-loading');
      // §162-36（Codex 指摘）古い通信は中止する（純正 predictive-search と同じ作法）
      this.facetsAbort?.abort();
      this.facetsAbort = new AbortController();

      // §216：絞り込みの一覧はブラウザの保存を使わない（cache: 'no-store'）＝見本商品を消した後もスマホの Chrome が古い一覧（41 件・消したブランド）を出し続けた 2026-09-29
      return fetch(this.facetsUrl(), { headers: { Accept: 'application/json' }, cache: 'no-store', signal: this.facetsAbort.signal })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then((data) => {
          if (seq !== this.reqSeq) return true; // 応答が前後したら最後の要求だけ採用
          // 台帳（全体像）と突き合わせ、消えた値を 0 件として戻してから描く
          return this.fetchMaster().then(() => {
            if (seq !== this.reqSeq) return true;
            const merged = this.mergeWithMaster(data);
            if (merged && Array.isArray(merged.filters)) merged.filters = this.decorateFilters(merged.filters);
            this.lastFacets = merged;
            this.render(merged);
            return true;
          });
        })
        .catch((err) => {
          if (seq !== this.reqSeq || err?.name === 'AbortError') return true; // 中断は失敗ではない
          this.renderError();
          return false; // 失敗を戻り値で伝える（再スローすると未処理の例外として漏れる）
        })
        .finally(() => {
          if (seq === this.reqSeq) this.el.classList.remove('is-loading');
        });
    }

    schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.fetchFacets(), DEBOUNCE_MS);
    }

    /* ---------- 描画 ---------- */
    render(data) {
      this.loadingEl?.remove();
      this.loadingEl = null;

      /* §162-39（user 報告）**0 件のページで開くと軸が 1 本も出ない**。
         真因＝Shopify は結果が 0 件だと `search.filters` を**空で返す**（絞る対象が無いため）。
         そのままだとパネルが空になり、**絞りすぎを緩める手段が消える＝行き止まり**。
         → 空が返ったら**直前に描けた軸の骨格を使い回す**（件数は 0 として描く）。 */
      const wanted = this.axesToShow.length ? this.axesToShow : null;
      let raw = data.filters || [];
      /* 骨格をまだ一度も持っていない状態で 0 件ページを開いた時（＝そのページの初回取得が
         いきなり空）は、**条件を外した素の軸一覧**を 1 回だけ取りに行って骨格にする。 */
      if (!raw.length && !this.lastAxes?.length && !this.skeletonTried) {
        this.skeletonTried = true;
        fetch(`${this.searchUrl}?q=*&view=facets&type=product`, { headers: { Accept: 'application/json' }, cache: 'no-store' })
          .then((r) => (r.ok ? r.json() : null))
          .then((d) => {
            if (!d?.filters?.length) return;
            this.lastAxes = this.decorateFilters(d.filters);
            if (this.lastFacets) this.render(this.lastFacets);
          })
          .catch(() => {});
      }
      if (!raw.length && this.lastAxes?.length) {
        raw = this.lastAxes.map((f) => ({
          ...f,
          values: (f.values || []).map((v) => ({ ...v, c: 0 })),
        }));
      } else if (raw.length) {
        this.lastAxes = raw;
      }
      this.zeroResult = Number(data.count || 0) === 0;
      /* 2026-10-03：大分類の軸（S&D）が来ていれば、細分類（product_type）は描かない
         （細分類は結果画面の役目）。来ていない間だけ product_type を従来のグループ表示で代役に立てる。
         product_type を選んだまま来た時（結果画面の絞り込みから開き直した等）もチップから外せる。 */
      const hasMajor = raw.some((f) => f.param === MAJOR);
      const list = raw
        .filter((f) => !wanted || wanted.includes(f.param))
        .filter((f) => !(hasMajor && f.param === TYPE))
        .filter((f) => !this.isUselessAxis(f));
      // data-axes に書いた順で並べる（S&D の並び順に引きずられない）
      if (wanted) list.sort((a, b) => wanted.indexOf(a.param) - wanted.indexOf(b.param));

      /* §162-30①（2026-08-08 user 指摘）再描画は innerHTML の丸ごと差し替え＝
         details の開閉が毎回初期値（先頭 1 軸だけ open）へ戻り、**1 つ選ぶたびに
         カテゴリーが閉じる**。差し替えの前に「いま開いている軸」を控え、後で戻す。 */
      this._openAxes = new Set(
        [...this.axesEl.querySelectorAll('details[data-param][open]')].map((d) => d.dataset.param)
      );
      this._hadAxes = !!this.axesEl.querySelector('details[data-param]');

      /* §179-15：中身が同じなら差し替えない（無用な描き直し＝アニメ再発火とチラつきの source） */
      const nextAxesHtml = list.map((f, i) => this.axisHtml(f, i)).join('');
      if (this.axesEl.innerHTML !== nextAxesHtml) {
        /* 開扉サイクル 2 回目以降の描き直しは、走行中の時差入場を打ち切って静かに差し替える */
        if (!this._enterPending) this.axesEl.classList.remove('ig-enter-run');
        this.axesEl.innerHTML = nextAxesHtml;
      }
      this.syncGroupHeads();
      /* この開扉サイクルの最初の描画＝時差入場を 1 回だけ */
      if (this._enterPending && this.dialog?.open) this.runEnterStagger();
      this.renderChips();
      this.updateCount(data.count);
      this.markScrollables();
      this.syncFootHeight();
      this.fetchPicks();
    }

    /* #4（2026-10-03）値が 1 つ以下の軸は描かない。
       例＝BADGE が「NEW」の 1 値だけ＝選ぶ余地が無い（全 37 商品に付いていて絞れもしない）。
       値が 2 つ以上あれば、絞り込みで 0 件になった値があっても軸は描き、その値は
       従来どおりグレー（.is-dead・選べない）で残す＝絞るほど軸が消える動きにしない
       （代表の実機確認 2026-10-03「選べない物はグレーアウトが一般的」）。
       値の数は台帳（全体像）と突き合わせた後で数える＝語や絞り込みで狭めても数は変わらない。
       ただし次は常に描く：
         - 価格（範囲の入力で値の一覧ではない）／在庫（入切のスイッチ 1 本）
         - その軸に選択中の値がある時（隠すと選んだ物を外せなくなる）
       一覧のフィルター（igniere-list-filter.liquid）も同じ条件で描かない。 */
    isUselessAxis(f) {
      if (f.type === 'price_range' || f.param === 'filter.v.price' || f.param === 'filter.v.availability') return false;
      const vals = Array.isArray(f.values) ? f.values : [];
      if (this.countSelected(f.param) > 0 || vals.some((v) => v.a === true)) return false;
      return vals.length <= 1;
    }

    /* §179-15：開扉サイクルの最初の描画にだけ、フィルターと同じ時差入場（520ms・80/115/150ms）を付ける */
    runEnterStagger() {
      this._enterPending = false;
      if (!this.axesEl) return;
      window.clearTimeout(this._enterTimer);
      this.axesEl.classList.remove('ig-enter-run');
      void this.axesEl.offsetWidth; // 直前の走行を確実に打ち切って再始動
      this.axesEl.classList.add('ig-enter-run');
      this._enterTimer = window.setTimeout(() => this.axesEl.classList.remove('ig-enter-run'), 700);
    }

    axisHtml(f, index) {
      // 在庫は「あり／切れ」の二択でなく、入／切のスイッチ 1 本で足りる（開閉させない）
      if (f.param === 'filter.v.availability') return this.stockSwitchHtml(f);

      const label = AXIS_LABEL_JA[f.param] || f.label;
      /*
       * §179-9（user 裁定 2026-08-13）初期は**全軸 closed**（フィルターと統一）。
       * 6 軸が目次として一望でき、送信ボタンも 1 画面に収まる。
       * （旧：1 軸だけ開く＝§162-17 期の値。開いた後の開閉状態の記憶 _openAxes は不変）
       */
      const open = (this._hadAxes ? this._openAxes.has(f.param) : false) ? ' open' : '';
      let body;
      if (f.type === 'price_range') body = this.priceHtml(f);
      else if (f.param === 'filter.p.vendor' && this.canTileBrands(f)) body = this.brandTilesHtml(f);
      else body = this.valuesHtml(f);
      /*
       * §162-17（2026-08-08 user 指摘「カテゴリーのアイテム数の表示が間違い」）
       * 見出しに出していた (n) は「**選んだ数**」だったが、値の行の (n) は「**商品の数**」。
       * 同じ書き方で意味が違うので読み違える（カテゴリーを 1 つ選ぶと「カテゴリー (1)」＝
       * 商品 1 点に見える）。**何を選んだかはチップが常に見せている**ので見出し側は要らない。
       */
      const picked = this.countSelected(f.param);

      return `<details class="ig-sp__grp${picked ? ' has-pick' : ''}" data-param="${esc(f.param)}"${open}>
  <summary class="ig-sp__sum">
    <span class="ig-sp__sum-lab">${esc(label)}${AXIS_SUB_JA[String(label).trim().toUpperCase()] ? `<span class="ig-axis-ja">${AXIS_SUB_JA[String(label).trim().toUpperCase()]}</span>` : ''}</span>
  </summary>
  <div class="ig-sp__grp-body">${body}</div>
</details>`;
    }

    /* ---------- ブランドをロゴの表で選ばせる（user 要望 2026-08-08） ----------
     * 条件（どれか欠けたら素直な文字の一覧に落ちる）：
     *   ①ブランドのロゴが揃っていること（1 つでも欠けると「表の中に文字だけの枠」ができて不揃いになる）
     *   ②数が多すぎないこと（既定 12 まで。増えたら**探すのはロゴより文字**が速い）
     * 大きさは Liquid が渡す等価正方形の幅（w%）。色は型抜きで単色化する
     * （ロゴの原色は明暗が両極で、明地に置くと薄い色が飛ぶため＝§16.28 と同じ理由）。
     */
    brandMap() {
      if (this._bmap) return this._bmap;
      const el = this.dialog?.querySelector('[data-ig-brand-map]');
      let list = [];
      try {
        list = JSON.parse(el?.textContent || '[]');
      } catch (_) {
        list = [];
      }
      this._bmap = new Map(list.map((x) => [String(x.n).trim(), x]));
      return this._bmap;
    }

    canTileBrands(f) {
      const max = Number(this.el.dataset.brandTilesMax || 12);
      const vals = f.values || [];
      if (!vals.length || vals.length > max) return false;
      const m = this.brandMap();
      return vals.every((v) => m.has(String(v.l).trim()) || m.has(String(v.v).trim()));
    }

    brandTilesHtml(f) {
      const m = this.brandMap();
      const chosen = this.selected.get(f.param) || new Set();
      const tiles = (f.values || [])
        .map((v) => {
          const info = m.get(String(v.l).trim()) || m.get(String(v.v).trim());
          const active = chosen.has(v.v);
          /* §163-20（user 指摘 2026-08-08）**0 件の時も暗転させる**。
             §162-39 で「0 件では暗転しない」と決めたのは、当時**語を消しても軸が
             戻らない不具合**があり、全部暗転すると戻れなくなるためだった。
             その不具合は §162-53 で直り、✕ でも打ち消しでも確実に戻る。
             前提が消えたので、仕様（無いものは選べない）へ揃える。 */
          const dead = v.c === 0 && !active;
          return `<label class="ig-sp__tile${active ? ' is-on' : ''}${dead ? ' is-dead' : ''}">
  <input type="checkbox" value="${esc(v.v)}" data-param="${esc(f.param)}"${active ? ' checked' : ''}${dead ? ' disabled' : ''}>
  <span class="ig-sp__tile-box">
    <span class="ig-sp__tile-logo" style="--ig-logo-src:url('${esc(info.u)}');--ig-logo-w:${info.w}%"></span>
    <span class="visually-hidden">${esc(v.l)}</span>
  </span>
  <span class="ig-sp__tile-cnt">(${v.c})</span>
</label>`;
        })
        .join('');
      return `<div class="ig-sp__tiles">${tiles}</div>`;
    }

    /* §175-2（2026-08-13 user 裁定）カテゴリー軸は絞り込みパネルと同じ
       「第一階層でグループ＋見出しで一括選択」。
       現れている値が 1 グループに収まる時は組まない（パネル側 §175 と同じ規則）
       グループ表（大分類→細分類）は直書きしない（2026-10-03）。正本は基幹の細分類表＋大分類表で、
       生成器（作業/tools/build_category_groups.py）が snippets/igniere-category-groups.liquid の
       <script class="ig-category-groups"> へ書き出す。ここはそれを読むだけ（無ければグループ化しない）。 */
    categoryGroups() {
      if (this._catGroups) return this._catGroups;
      const el = document.querySelector('script.ig-category-groups');
      if (!el) return []; /* まだ無い時は覚えない（後で読み直せるように） */
      let list = [];
      try {
        const parsed = JSON.parse(el.textContent);
        if (Array.isArray(parsed)) list = parsed;
      } catch (_) {
        list = [];
      }
      /* 形＝[{code, name, handle, types:[細分類…]}, …]（2026-10-03 から。並び＝大分類表の優先順位）。
         旧形 [name, types] が来ても読めるようにそろえておく（生成物と JS の入れ違いで落とさない） */
      this._catGroups = list
        .map((g) => (Array.isArray(g) ? { code: null, name: g[0], handle: null, types: g[1] } : g))
        .filter((g) => g && typeof g.name === 'string')
        .map((g) => ({ code: g.code || null, name: g.name, handle: g.handle || null, types: Array.isArray(g.types) ? g.types : [] }));
      return this._catGroups;
    }

    /** 大分類の記号 → 対応表の 1 行（無ければ null） */
    majorGroup(code) {
      return this.categoryGroups().find((g) => g.code === code) || null;
    }

    /* 大分類の値を表示名へ置き換え、対応表の順（大分類表の優先順位）に並べ替える。
       facets（S&D）の並び＝件数順に引きずられない。対応表に無い記号は記号のまま末尾へ。 */
    decorateFilters(filters) {
      if (!Array.isArray(filters)) return filters;
      /* §277（D16・2026-10-06 user 裁定）BRAND の「NO BRAND」は選ぶ理由が無い（ブランドの名前に見える）ので出さない。
         選択中の時だけは外せるように残す。一覧の絞り込み（igniere-list-filter.liquid）も同じ */
      filters = filters.map((f) => {
        if (f.param !== 'filter.p.vendor' || !Array.isArray(f.values)) return f;
        return Object.assign({}, f, { values: f.values.filter((v) => v.a === true || String(v.v).toUpperCase() !== 'NO BRAND') });
      });
      const groups = this.categoryGroups();
      if (!groups.length) return filters;
      const order = new Map(groups.map((g, i) => [g.code, i]));
      return filters.map((f) => {
        if (f.param !== MAJOR || !Array.isArray(f.values)) return f;
        const values = f.values
          .map((v, i) => {
            const g = this.majorGroup(v.v);
            return { v: Object.assign({}, v, { l: g ? g.name : v.v }), k: order.has(v.v) ? order.get(v.v) : groups.length + i };
          })
          .sort((a, b) => a.k - b.k)
          .map((x) => x.v);
        return Object.assign({}, f, { values });
      });
    }

    groupedTypeHtml(f) {
      const GROUPS = this.categoryGroups();
      if (!GROUPS.length) return null;
      const vals = f.values || [];
      const byV = new Map(vals.map((v) => [v.v, v]));
      const present = GROUPS
        .map((g) => [g.name, g.types.filter((t) => byV.has(t))])
        .filter((g) => g[1].length);
      const known = new Set();
      present.forEach((g) => g[1].forEach((t) => known.add(t)));
      const rest = vals.filter((v) => !known.has(v.v)).map((v) => v.v);
      if (rest.length) present.push(['その他', rest]);
      if (present.length < 2) return null;

      const chosen = this.selected.get(f.param) || new Set();
      const row = (v) => {
        const active = chosen.has(v.v);
        const dead = v.c === 0 && !active;
        return `<label class="ig-sp__val${dead ? ' is-dead' : ''}">
  <input type="checkbox" value="${esc(v.v)}" data-param="${esc(f.param)}"${active ? ' checked' : ''}${dead ? ' disabled' : ''}>
  <span class="ig-sp__val-lab">${esc(v.l)}</span>
  <span class="ig-sp__val-cnt">(${v.c})</span>
</label>`;
      };
      return present
        .map(([name, list]) => {
          const selectable = list.filter((t) => { const v = byV.get(t); return !(v.c === 0 && !chosen.has(t)); });
          const allOn = selectable.length > 0 && selectable.every((t) => chosen.has(t));
          const someOn = list.some((t) => chosen.has(t));
          const state = allOn ? 'all' : (someOn ? 'some' : 'none');
          /* 見出しの箱＝本物のチェックボックス（2026-10-03・絞り込みパネルと同じ形）。
             label に ig-sp__val も付ける＝値の箱と同じ CSS（ネイティブ 18px・§179-10）がそのまま当たり、見た目が一致する。
             全選択＝checked／一部＝indeterminate（render 後に syncGroupHeads が入れる）／なし＝空。
             data-param を持たない＝値の change（toggleValue）には乗らない */
          return `<div class="ig-fpg">
  <label class="ig-sp__val ig-fpg__head" data-state="${state}">
    <input type="checkbox" class="ig-fpg__cb" data-ig-sp-group="${esc(f.param)}"${allOn ? ' checked' : ''}${selectable.length ? '' : ' disabled'}><span class="ig-fpg__name">${esc(name)}</span>
  </label>
  <div class="ig-fpg__items">${list.map((t) => row(byV.get(t))).join('')}</div>
</div>`;
        })
        .join('');
    }

    /** 見出しの箱の一部選択（横棒）は属性で書けない＝描画のたびにプロパティで入れ直す */
    syncGroupHeads() {
      this.axesEl?.querySelectorAll('.ig-fpg__head .ig-fpg__cb').forEach((cb) => {
        const st = cb.closest('.ig-fpg__head').dataset.state;
        cb.checked = st === 'all';
        cb.indeterminate = st === 'some';
      });
    }

    valuesHtml(f) {
      if (f.param === TYPE) {
        const grouped = this.groupedTypeHtml(f);
        if (grouped) return grouped;
      }
      const chosen = this.selected.get(f.param) || new Set();
      return (f.values || [])
        .map((v) => {
          const active = chosen.has(v.v);
          /* 裁定 #4：0 件かつ未選択なら選べない（選択済みは残す＝解除できなくなるため）。
             §162-39：ただし**結果が 0 件の時は暗転しない**。全部が 0 件になるので、
             暗転すると「条件を緩めて別の値を選ぶ」道まで塞がる＝行き止まりになる。 */
          /* §163-20（user 指摘 2026-08-08）**0 件の時も暗転させる**。
             §162-39 で「0 件では暗転しない」と決めたのは、当時**語を消しても軸が
             戻らない不具合**があり、全部暗転すると戻れなくなるためだった。
             その不具合は §162-53 で直り、✕ でも打ち消しでも確実に戻る。
             前提が消えたので、仕様（無いものは選べない）へ揃える。 */
          const dead = v.c === 0 && !active;
          return `<label class="ig-sp__val${dead ? ' is-dead' : ''}">
  <input type="checkbox" value="${esc(v.v)}" data-param="${esc(f.param)}"${active ? ' checked' : ''}${dead ? ' disabled' : ''}>
  <span class="ig-sp__val-lab">${esc(v.l)}</span>
  <span class="ig-sp__val-cnt">(${v.c})</span>
</label>`;
        })
        .join('');
    }

    /*
     * 在庫のスイッチ（user 提案 2026-08-08）
     * 入＝在庫のある商品だけ（filter.v.availability=1）／切＝すべて見せる（指定なし）
     * 形は角のある矩形。丸いスイッチは §8.1 の 5 段階で「50% は nav 円形アイコン・swatch・
     * avatar のみ」と限定されているため使わない（§0.8 のスタイル規則・裁定は user）。
     */
    stockSwitchHtml(f) {
      const on = (this.selected.get(f.param) || new Set()).has('1');
      /* §255（2026-10-03）件数は出さない。Shopify の在庫の軸の件数は当てにならない
         （一覧では 0・検索の応答では 37、実際に絞ると 32）。スイッチは入れれば結果の件数が変わるので数は要らない。 */
      return `<div class="ig-sp__stock">
  <label class="ig-sp__sw-row">
    <span class="ig-sp__sw-lab">在庫のある商品だけ</span>
    <input type="checkbox" role="switch" class="ig-sp__sw" data-param="${esc(f.param)}" value="1"${on ? ' checked' : ''} aria-label="在庫のある商品だけを表示">
    <span class="ig-sp__sw-track" aria-hidden="true"><span class="ig-sp__sw-knob"></span></span>
  </label>
</div>`;
    }

    priceHtml(f) {
      const max = Number(f.range_max || 0);
      const gte = this.price.gte != null ? this.price.gte : '';
      const lte = this.price.lte != null ? this.price.lte : '';
      return `<div class="ig-sp__price">
  <label class="ig-sp__price-fld">
    <span class="visually-hidden">価格の下限</span>
    <input type="number" inputmode="numeric" min="0" step="1000" placeholder="下限" value="${gte}" data-price="gte">
  </label>
  <span class="ig-sp__price-sep" aria-hidden="true">〜</span>
  <label class="ig-sp__price-fld">
    <span class="visually-hidden">価格の上限</span>
    <input type="number" inputmode="numeric" min="0" step="1000" placeholder="上限" value="${lte}" data-price="lte">
  </label>
  <span class="ig-sp__price-note">最高 ${esc(yen(max))}</span>
</div>`;
    }

    renderChips() {
      const chips = [];
      for (const [param, values] of this.selected) {
        // 在庫はスイッチ自身が状態を示すので、チップに二重で出さない
        if (param === 'filter.v.availability') continue;
        for (const v of values) {
          const lab = this.labelFor(param, v);
          chips.push(
            `<button type="button" class="ig-sp__chip" data-param="${esc(param)}" data-value="${esc(v)}">${esc(lab)}<span class="ig-sp__chip-x" aria-hidden="true">×</span><span class="visually-hidden">を解除</span></button>`
          );
        }
      }
      if (this.price.gte != null || this.price.lte != null) {
        const a = this.price.gte != null ? '¥' + this.price.gte.toLocaleString('ja-JP') : '';
        const b = this.price.lte != null ? '¥' + this.price.lte.toLocaleString('ja-JP') : '';
        chips.push(
          `<button type="button" class="ig-sp__chip" data-price-clear>${esc(a)}〜${esc(b)}<span class="ig-sp__chip-x" aria-hidden="true">×</span><span class="visually-hidden">を解除</span></button>`
        );
      }
      const has = chips.length > 0;
      /* §162-33（2026-08-08 user 指摘）横送り＋溶けをやめる。
         狭い画面では 2 個目から切れ、指で横に送らせるのは成立しない（user 報告）。
         **入る分だけ出し、あふれた分は「＋N」に畳む**＝個数でなく実測幅で決まる。 */
      this.chipsEl.innerHTML = chips.join('') + '<span class="ig-sp__chip-more" hidden></span>';
      this.chipsEl.hidden = !has;
      if (this.clearEl) this.clearEl.hidden = false; /* §179-9：フィルターと同じく常時表示 */
      // §162-31③：見出し行の同じ席をラベルとチップで分け合う（行の高さは変わらない）
      this.el.classList.toggle('has-chips', has);
      if (has) requestAnimationFrame(() => this.fitChips());
      // キーワード入力中でも、軸が選ばれていれば送信ボタンを出す（CSS 側で使う目印）
      this.el.classList.toggle('has-pick', this.hasSelection());
    }

    /* 入る分だけ見せ、あふれた分を「＋N」に畳む（幅で判定＝画面と文字数に自動で追随） */
    fitChips() {
      const more = this.chipsEl.querySelector('.ig-sp__chip-more');
      const items = [...this.chipsEl.querySelectorAll('.ig-sp__chip')];
      if (!more || !items.length) return;

      items.forEach((c) => (c.hidden = false));
      more.hidden = true;

      const avail = this.chipsEl.clientWidth;
      if (!avail) return;

      const gap = parseFloat(getComputedStyle(this.chipsEl).columnGap) || 16;
      // 「＋N」の実幅を測るため、いったん最大値で描いて幅を取る
      more.hidden = false;
      more.textContent = `＋${items.length}`;
      const moreW = more.getBoundingClientRect().width;

      let used = 0;
      let shown = 0;
      for (const c of items) {
        const w = c.getBoundingClientRect().width;
        const next = used + (shown ? gap : 0) + w;
        // 最後の 1 個が入りきる時は「＋N」の席を取らない
        const isLast = shown === items.length - 1;
        const need = next + (isLast ? 0 : gap + moreW);
        if (need <= avail) {
          used = next;
          shown += 1;
        } else break;
      }

      items.forEach((c, i) => (c.hidden = i >= shown));
      const rest = items.length - shown;
      if (rest > 0) {
        more.hidden = false;
        more.textContent = `＋${rest}`;
      } else {
        more.hidden = true;
      }
    }

    /* §163-28（Codex 再指摘・2026-08-09）**候補のキーボード操作は持たない**。

       §162-36② は「role="combobox" を名乗る以上、↓↑ で候補へ行けねばならない」
       という理由で自前の候補送りを入れていた。その後 §163-7 で role="combobox" を
       外し、ただの検索欄にしたので、前提そのものが無くなった。
       ただの検索欄では Tab で候補のリンクへ行くのが標準の作法であり、実測でも
       Tab で全候補に到達できる。逆に自前の候補送りは、候補の入れ替わりで
       焦点が本文へ飛ぶ壊れ方を実測したため、残さずに外す。
       純正の on:keydown を外す処理（下記）はそのまま残す＝隠した純正候補の
       キーボード操作を復活させないため。 */


    labelFor(param, value) {
      // 大分類は記号を見せない（facets を取る前の復元チップでも表示名にする）
      if (param === MAJOR) {
        const g = this.majorGroup(value);
        if (g) return g.name;
      }
      const f = (this.lastFacets?.filters || []).find((x) => x.param === param);
      const v = f?.values?.find((x) => x.v === value);
      return v?.l || value;
    }

    countSelected(param) {
      if (param === 'filter.v.price') return this.price.gte != null || this.price.lte != null ? 1 : 0;
      return (this.selected.get(param) || new Set()).size;
    }

    updateCount(n) {
      const c = Number(n || 0);
      // §20.1：件数は文に溶かす（裸の数字を置かない）
      /* §163-22（user 指摘 2026-08-08）**0 件でも送信できるようにする**。
         旧＝0 件でボタンを無効にし、Enter も封じていた（§163-21）。これは
         検索の作法として一般的でない。世間の検索は 0 件でも結果ページを出し、
         「見つかりませんでした」と伝える。押せないボタンは**理由を伝えず**、
         壊れているようにも見え、探した記録（URL）も残らず共有・再訪ができない。
         さらに 0 件の面（§163-18）を作ってあるのに、そこへ行く道を自分で塞いでいた。
         → 送れるようにし、文言は「結果を見る」に統一する（件数が 0 でも同じ動作）。
         軸の暗転（無いものは選べない・§163-20）はそのまま。あれは**選択肢**の話で、
         **送信の可否**とは別の事柄。 */
      const label = c > 0 ? `${c.toLocaleString('ja-JP')} 件を見る` : '結果を見る';
      if (this.submitLabelEl) this.submitLabelEl.textContent = label;
      if (this.submitEl) this.submitEl.disabled = false;
      requestAnimationFrame(() => this.syncFootHeight());
      if (this.statusEl) this.statusEl.textContent = `${c} 件`;
    }

    /*
     * §162-23 貼り付けた送信の帯の高さを測って、中身の下敷きに使う。
     * 決め打ちにしないのは、文言（「該当する商品がありません」）で 1 行増えると帯が伸びるため。
     */
    syncFootHeight() {
      const foot = this.submitEl?.closest('.ig-sp__foot');
      if (!foot || !this.dialog) return;
      /* §162-31②：下敷きが要るのは**器が実際にスクロールする時だけ**。
         収まっている時は sticky の帯が自然位置にあり何も覆わないので、
         下敷きはそのまま「冗長な空き」になる（user 指摘）。 */
      const scrolls = this.dialog.scrollHeight > this.dialog.clientHeight + 2;
      const h = scrolls ? Math.ceil(foot.getBoundingClientRect().height) : 0;
      this.dialog.style.setProperty('--ig-sp-foot-h', h + 'px');
    }

    /*
     * §162-13 続きがあることを示す。
     * 中で切れている軸だけ下端を地へ溶かし、最後まで送ったら解く。
     * 線や矢印を足さずに「まだ下がある」を伝える。
     */
    markScrollables() {
      this.el.querySelectorAll('.ig-sp__grp-body').forEach((body) => {
        const update = () => {
          const overflows = body.scrollHeight > body.clientHeight + 2;
          body.classList.toggle('is-short', !overflows);
          const atEnd = body.scrollTop + body.clientHeight >= body.scrollHeight - 2;
          body.classList.toggle('is-end', overflows && atEnd);
        };
        update();
        if (!body.__igBound) {
          body.__igBound = true;
          body.addEventListener('scroll', update, { passive: true });
        }
        // 軸を開いた直後は高さが確定していないので 1 コマ待って測り直す
        requestAnimationFrame(update);
      });
    }

    /* ---------- 今の条件に合う商品を最大 4 点（§162-14・user 裁定 案 A） ----------
     * 何も選んでいない時は出さない。選んだ瞬間に、その条件の商品が絵で返ってくる。
     * 出ている間は「最近閲覧した商品」を畳む（同じ場所を役割で分け合う）。
     */
    fetchPicks() {
      if (!this.picksEl) return;

      /* §162-58（user 裁定 2026-08-08）商品表示を**この 1 つに一本化**する。
         旧＝軸を選んだ時だけ出し、語がある時は純正の候補に場所を譲っていた。
         その結果、①形が違う（純正は格子・SP で 2 段／こちらは横 1 行）
         ②中身が違う（純正は**語だけ**を見るので、語＋軸で絞っても
         軸を無視した商品が並び、送信ボタンの件数と食い違う）という
         2 つの不整合が出ていた（実測：語＋ブランドで送信は 1 件なのに候補は 4 点）。
         → **語があるか軸があれば出す**。中身は語と軸の両方を効かせた条件で取る。 */
      const hasQuery = !!(this.input?.value || '').trim();

      /* §162-36（Codex 指摘）解除時に連番を進めていなかったため、解除の直前に投げた応答が
         後から着くと**消したはずの候補が戻る**。解除も 1 手として数える。 */
      if (!this.hasSelection() && !hasQuery) {
        this.picksSeq += 1;
        this.picksAbort?.abort();
        this.picksEl.hidden = true;
        this.picksEl.innerHTML = '';
        this.dialog?.classList.remove('ig-has-picks');
        return;
      }

      const seq = ++this.picksSeq;
      this.picksAbort?.abort();
      this.picksAbort = new AbortController();
      fetch(this.buildUrl({ view: 'picks', type: 'product' }), { headers: { Accept: 'application/json' }, signal: this.picksAbort.signal })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then((d) => {
          if (seq !== this.picksSeq) return;
          const items = d.items || [];
          if (!items.length) {
            this.picksEl.hidden = true;
            this.picksEl.innerHTML = '';
            this.dialog?.classList.remove('ig-has-picks');
            return;
          }
          /* #5（2026-10-03）件数は送信ボタン「N 件を見る」の 1 か所に集める。
             旧＝ここにも「ほか N 点」を出しており、同じ件数が 2 か所に別の言い方で並んでいた。 */
          this.picksEl.innerHTML = `<div class="ig-sp__picks-head">
  <span class="ig-sp__picks-lab">${this.hasSelection() ? 'この条件の商品' : '商品'}</span>
</div>
<ul class="ig-sp__picks-list">${items.map((it) => this.pickHtml(it)).join('')}</ul>`;
          this.picksEl.hidden = false;
          this.dialog?.classList.add('ig-has-picks');
        })
        .catch((err) => {
          if (seq !== this.picksSeq || err?.name === 'AbortError') return;
          this.picksEl.hidden = true;
          this.picksEl.innerHTML = '';
          this.dialog?.classList.remove('ig-has-picks');
        });
    }

    pickHtml(it) {
      const img = it.img
        ? `<img class="ig-sp__pick-im" src="${esc(it.img)}" alt="${esc(it.alt || '')}" width="400" height="500" loading="lazy" decoding="async">`
        : '<span class="ig-sp__pick-im ig-sp__pick-im--none" aria-hidden="true"></span>';
      const sale = Number(it.sale || 0) > 0;
      return `<li class="ig-sp__pick">
  <a href="${esc(it.u)}">
    ${img}
    <span class="ig-sp__pick-t">${esc(it.t)}</span>
    <span class="ig-sp__pick-p${sale ? ' is-sale' : ''}">${esc(it.price)}</span>
  </a>
</li>`;
    }

    renderError() {
      this.loadingEl?.remove();
      this.loadingEl = null;
      this.axesEl.innerHTML = '<p class="ig-sp__err">絞り込みを読み込めませんでした。キーワードで検索できます。</p>';
    }

    /* ---------- 操作 ---------- */
    toggleValue(param, value, on) {
      if (!this.selected.has(param)) this.selected.set(param, new Set());
      const set = this.selected.get(param);
      if (on) set.add(value);
      else set.delete(value);
      if (!set.size) this.selected.delete(param);
      this.schedule();
    }

    clearAll() {
      this.selected.clear();
      this.price = { gte: null, lte: null };
      this.schedule();
    }

    /* §172（2026-08-13 user 裁定）着地先の分岐＝役割の明確化。
       キーワードあり → 検索結果ページ（キーワードの答えの場所・従来どおり）。
       キーワードなし（軸だけ・または空）→ **全商品一覧に絞り込みを適用して着地**
       ＝軸検索は「カタログの絞り込み」であり、フィルターの本場（コレクションページ）に立つ。
       これで擬似カタログ（「商品をさがす」の顔をした検索結果）は動線から消える。
       絞り込みのパラメーター名（filter.p.*／filter.v.*）は検索もコレクションも同一（S&D 標準）。 */
    /* 2026-10-03（代表裁定）大分類をちょうど 1 つ選び、言葉なし・細分類なし → その大分類の棚へ。
       棚が実在しない／0 件なら null（全商品一覧に絞り込みで落とす＝行き止まりを作らない） */
    majorShelf(typed) {
      if (typed) return null;
      const majors = this.selected.get(MAJOR);
      if (!majors || majors.size !== 1) return null;
      if ((this.selected.get(TYPE) || new Set()).size) return null;
      const g = this.majorGroup([...majors][0]);
      if (!g || !g.handle) return null;
      const n = Number(this.majorCols[g.handle]);
      if (!Number.isFinite(n) || n <= 0) return null;
      return `${this.collectionsUrl}/${encodeURIComponent(g.handle)}`;
    }

    submit() {
      const typed = (this.input?.value || '').trim();
      if (!typed) {
        const shelf = this.majorShelf(typed);
        const params = new URLSearchParams();
        for (const [param, values] of this.selected) {
          if (shelf && param === MAJOR) continue; // 棚そのものが大分類＝重ねて付けない（他の軸は引き継ぐ）
          for (const v of values) params.append(param, v);
        }
        if (this.price.gte != null) params.set('filter.v.price.gte', String(this.price.gte));
        if (this.price.lte != null) params.set('filter.v.price.lte', String(this.price.lte));
        const qs = params.toString();
        const base = shelf || this.allUrl;
        window.location.href = qs ? `${base}?${qs}` : base;
        return;
      }
      window.location.href = this.buildUrl({ type: 'product' });
    }

    /* ---------- 入力の有無で「候補」と「軸」を出し分ける ---------- */
    /*
     * 理由（2026-08-08 実測）：純正の入力補助は、キーワードが空でも既定の商品を 4 点出す。
     * SP ではこれだけで 684px あり、軸に辿り着く前に画面が埋まる。
     * 言葉が浮かんでいる人には候補・浮かんでいない人には軸、と役割で出し分ける。
     */
    syncMode() {
      const typed = (this.input?.value || '').trim().length > 0;
      this.dialog?.classList.toggle('ig-has-query', typed);
      this.dialog?.classList.toggle('ig-no-query', !typed);
    }

    /* ---------- 配線 ---------- */
    bind() {
      /* §162-24（2026-08-08 user 実機報告で是正・最重要）
         純正 predictive-search-component は「自分の中の全クリックで入力欄へ焦点を戻す」
         （predictive-search.js #handleModalClick）。除外は button / a / input の**祖先**だけで、
         label・summary・タイルの箱は対象外＝**軸のどこを触っても端末のキーボードが立ち上がる**。
         軸ブロックの操作は部品に見せない（click / keydown をここで止める。
         同じ要素に付けた自分の他の listener は止まらない＝機能はそのまま）。 */
      this.el.addEventListener('click', (e) => e.stopPropagation());
      this.el.addEventListener('keydown', (e) => {
        e.stopPropagation();
        /* §162-30②：軸の中の Enter（数字キーボードの「完了」）は「適用」＝結果ページへ飛ばない。
           暗黙送信はフォーム既定の送信ボタン（純正「すべて表示」＝type 無し）を**押しに行く**ため、
           submit イベントの横取りでは捕まらない（実測）。keydown の時点で既定動作ごと止める。 */
        /* §162-31④（user 指摘）日本語入力の確定 Enter を拾うと、確定と自分の処理が
           二重に走って「1000」が「10001000」になる。変換中の Enter は素通しする
           （isComposing／keyCode 229 は IME 変換中の合図）。 */
        if (e.key === 'Enter' && e.target.matches('input') && !e.isComposing && e.keyCode !== 229) {
          e.preventDefault();
          e.target.blur();
          this.schedule();
        }
      });

      if (this.input) {
        /* §162-36③（Codex 指摘）キーワードを変えても件数と 0 件暗転が更新されず、
           表示件数が「語 AND 軸」の結果でなくなっていた。語の変更も取り直しの対象にする。 */
        /* §162-39：`loadedOnce` を条件にしていたため、**0 件ページで骨格だけを持っている状態**
           （＝有効な取得がまだ 1 度も無い）では語を変えても取り直しが走らず、
           「該当する商品がありません」から二度と戻れなかった（実測＝通信 0 件）。
           語が変わったら常に取り直す。 */
        const onKeyword = () => {
          this.syncMode();
          this.schedule();
          // ✕ の出し入れを入力値と一致させる（復元・クリアの両方向で狂わせない）
          const rs = this.dialog?.querySelector('.predictive-search__reset-button');
          if (rs) rs.toggleAttribute('hidden', !(this.input?.value || '').trim());
        };
        this.input.addEventListener('input', onKeyword);
        this.input.addEventListener('change', onKeyword);

        /* §162-53（user 報告 2026-08-08）「✕ で語を消しても、軸と件数が古いまま」。
           原因＝**純正の ✕ は値をプログラムで空にする**。プログラムでの代入では
           `input` イベントが出ないので、こちらの更新処理が一度も走らない。
           （軸が語で絞られること自体は仕様＝「無いものは選べない」。
             語を消したら絞りも解けるのが筋なのに、そこだけ取り残されていた。）
           → ✕ の押下を拾って、打ち直した時と同じ処理を通す。
           押した直後はまだ値が残っているため、純正が空にし終える次の番で走らせる。 */
        const resetBtn = this.dialog?.querySelector('.predictive-search__reset-button');
        if (resetBtn) {
          resetBtn.addEventListener('click', () => {
            requestAnimationFrame(() => {
              if (this.input) this.input.value = '';
              onKeyword();
            });
          });
        }
      }
      this.syncMode();

      // 値のチェック
      this.el.addEventListener('change', (e) => {
        const cb = e.target.closest('input[type="checkbox"][data-param]');
        if (cb) {
          this.toggleValue(cb.dataset.param, cb.value, cb.checked);
          return;
        }
        const pr = e.target.closest('input[data-price]');
        if (pr) {
          /* §162-30③：URL の価格パラメータの単位は**円**（実測＝gte=10000 → 19 件・
             銭 1000000 だと 0 件）。旧実装は銭に換算して送っており全滅していた */
          const raw = pr.value.trim();
          const yenV = raw === '' ? null : Math.max(0, Math.round(Number(raw)));
          this.price[pr.dataset.price] = Number.isFinite(yenV) ? yenV : null;
          this.schedule();
        }
      });

      // チップの解除・すべて解除・送信
      this.el.addEventListener('click', (e) => {
        /* §175-2 グループ見出し＝配下（暗転を除く）を一括選択/解除 */
        /* 見出しは label＋箱＝文字を押すと label と箱の 2 回 click が来る→箱の 1 回だけ拾う */
        const gh = e.target.closest('input.ig-fpg__cb[data-ig-sp-group]');
        if (gh) {
          const param = gh.dataset.igSpGroup;
          const boxes = [...gh.closest('.ig-fpg').querySelectorAll('.ig-fpg__items input[type="checkbox"][data-param]')].filter((i) => !i.disabled);
          const allOn = boxes.length > 0 && boxes.every((i) => i.checked);
          boxes.forEach((i) => {
            i.checked = !allOn; // 取り直しを待たずに見た目を合わせる
            this.toggleValue(param, i.value, !allOn);
          });
          gh.indeterminate = false;
          gh.checked = !allOn && boxes.length > 0;
          return;
        }
        const chip = e.target.closest('.ig-sp__chip[data-param]');
        if (chip) {
          this.toggleValue(chip.dataset.param, chip.dataset.value, false);
          return;
        }
        if (e.target.closest('[data-price-clear]')) {
          this.price = { gte: null, lte: null };
          this.schedule();
          return;
        }
        if (e.target.closest('[data-ig-sp-clear]')) {
          this.clearAll();
          return;
        }
      });

      // 送信ボタンとクリアは .ig-sp の外にあるので、器側で拾う
      this.submitEl?.addEventListener('click', () => this.submit());
      this.clearEl?.addEventListener('click', () => this.clearAll());

      // 軸を開いた／閉じたら、溶かす必要があるかを測り直す
      this.el.addEventListener('toggle', () => this.markScrollables(), true);

      // 純正フォームの送信を握る（Enter で軸が落ちるのを防ぐ）
      if (this.form) {
        /* §162-30④（構造対策）：暗黙送信を根から絶つ。
           実キーの Enter は、順序の勝負（capture の純正委譲が先）になり preventDefault が
           間に合わない経路があった（合成イベントでは prevented=True なのに実キーで飛ぶ＝実測）。
           フォーム既定の送信ボタン＝純正「すべて表示」（type 無し＝submit）を type=button 化し、
           **submit 型のボタンをフォームから無くす**＝多入力フォームでは Enter の暗黙送信自体が
           起きなくなる（仕様）。キーワードの Enter と「すべて表示」は自前の送信 1 関数へ接続
           （q も軸も同じ URL 組み立てに乗る＝挙動が 1 本化される）。 */
        /* §162-36② rev2（Codex 指摘への最終形）
           純正の `on:keydown` は **document の capture** で受けられており（component.js:239）、
           DOM の中に置いた listener は順序で必ず負ける＝Enter の暴発は止められない
           （実測＝価格欄の Enter で `/search?q=` へ飛ぶ）。
           → **純正の配線は外す**。
           §163-28（2026-08-09）当初はここで自前の候補送りも足していたが、
           §163-7 で role="combobox" を外してただの検索欄にしたため前提が消えた。
           候補へは Tab で行く（標準の作法・実測で到達を確認）。 */
        this.form.removeAttribute('on:keydown');
        this.input?.removeAttribute('on:keydown');

        const viewAll = this.form.querySelector('[ref="viewAllButton"]');
        if (viewAll) {
          viewAll.setAttribute('type', 'button');
          viewAll.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.submit();
          });
        }
        /* Enter だけを capture で先取りする（純正の onSearchKeyDown より先に走る）。
           §162-36④：**日本語入力の確定 Enter は素通し**（価格欄と同じガード。
           これが無いと変換確定でそのまま検索へ飛ぶ）。 */
        /* ⚠ 純正の配線（on:keydown）は**部品の側で capture 受け**されるため、
           form や .ig-sp に付けた listener は順序で必ず負ける（§162-30 で
           「実キーだと飛ぶ」と観測したのはこれ）。**器（dialog）の capture** に置けば
           部品より外側なので確実に先へ立てる。Enter 以外は一切触らない
           （矢印・Tab の候補送りは純正のまま生きる）。 */
        this.dialog?.addEventListener(
          'keydown',
          (e) => {
            if (e.key !== 'Enter') return;
            if (e.isComposing || e.keyCode === 229) return; // 日本語入力の確定は素通し
            const t = e.target;
            if (!(t instanceof HTMLElement)) return;
            if (t.closest('.ig-sp')) {
              // 軸の中（価格など）＝「適用」。飛ばずに件数を取り直すだけ
              e.preventDefault();
              e.stopPropagation();
              t.blur();
              this.schedule();
              return;
            }
            if (t !== this.input) return; // 候補リンク等の Enter は純正に任せる
            e.preventDefault();
            e.stopPropagation();
            /* §163-22：送信ボタンが押せない時は Enter でも飛ばさない（経路で結果を
               変えない）。ただし 0 件では無効にしない方針へ変えたので、通常は通る。 */
            if (this.submitEl?.disabled) return;
            this.submit();
          },
          true
        );
        this.form.addEventListener(
          'submit',
          (e) => {
            /* §162-30②（2026-08-08 user 指摘）価格欄で Enter（数字キーボードの「完了」）を
               押すとフォームが送信され、結果ページへ飛んでいた＝下限だけ入れた時点で離陸し、
               上限やブランドとの掛け合わせができない。**軸の中からの送信は「適用」**＝
               飛ばずに件数を取り直すだけ。飛ぶのはキーワード欄の Enter と送信ボタンだけ。 */
            const ae = document.activeElement;
            if (ae && ae.closest && ae.closest('.ig-sp')) {
              e.preventDefault();
              e.stopPropagation();
              this.schedule();
              return;
            }
            if (!this.hasSelection()) return; // 軸が無い時は純正のまま
            e.preventDefault();
            e.stopPropagation();
            this.submit();
          },
          true
        );
      }

      /* §162-25（2026-08-08 user 実機報告で是正）メニュー排他＝§16.24-1。
         器は showModal（背景が不活性）なので、開いたままハンバーガー等を押すと
         タップは背面幕に当たり「閉じるだけ」で終わっていた＝ワンタップで切り替わらない。
         幕に当たった点の下にヘッダーの操作（ハンバーガー・カート・アカウント・ナビ）が
         あれば、閉じた直後にそれを押し直す＝**1 回のタップで乗り換え**。 */
      if (this.dialog) {
        /* ⚠ WebKit（iPhone）は幕へのタップの的が html になり click も合成されない
           （実測 2026-08-08：pointerdown:HTML のみ・click 無し）＝純正の
           「外側タップで閉じる」(on:click=closeDialogOnClickOutside) が**触では発火しない**。
           文書側の pointerdown（的でなく座標で判定）で受ける。 */
        document.addEventListener('pointerdown', (e) => {
          if (!this.dialog.open) return;
          /* §179-7：SP のヘッダー直下シート（§179）では「外側」＝ヘッダーだけで、
             ヘッダーの部品は直接押せる。この転送（閉じてから座標の部品を click し直す）を
             SP で残すと**二重発火**になる——ハンバーガーが開いた 340ms 後（dialog.close の
             close イベント）に再クリックされ、即閉じる（user 実機報告 2026-08-13）→ PC 限定 */
          if (window.matchMedia('(max-width: 989px)').matches) return;
          const r = this.dialog.getBoundingClientRect();
          const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
          if (inside) return; // 中身の操作は通常どおり
          this._switchPoint = { x: e.clientX, y: e.clientY };
          const closeBtn = this.dialog.querySelector('.predictive-search__close-modal-button');
          if (closeBtn) closeBtn.click();
          else this.dialog.close();
        }, true);
        this.dialog.addEventListener('close', () => {
          const pt = this._switchPoint;
          this._switchPoint = null;
          if (!pt) return;
          requestAnimationFrame(() => {
            const el = document.elementFromPoint(pt.x, pt.y);
            const trigger = el && el.closest('.header-section :is(button, summary, a)');
            if (trigger && !trigger.closest('search-button')) trigger.click();
          });
        });
      }

      // 開く前に先読みしておく（間に合わなかった時の保険として開いた時にも呼ぶ＝二重取得はしない）
      window.addEventListener('resize', () => {
        if (this.dialog?.open && !this.chipsEl.hidden) this.fitChips();
      });

      /* §162-38②（user 実機報告「数秒待ってから開くと背後のページがスクロールされる」）
         これまで「位置を凍らせる」→「ヘッダーに追従させる」と対症で往復したが、
         **根は「開いている間に背後が動けること」1 点**。動けなくすれば、凍結も追従も要らない。

         `overscroll-behavior: contain` は**器の中の払いが行き止まりで漏れるの**を止めるだけで、
         **器の外（ヘッダー帯・幕）を払った指**は素通しになる。iOS はそこからページを動かす。
         読み込み直後に起きないのは、その時点でページがまだ伸びておらず動かせないから
         （数秒後に画像や節が載って初めて動けるようになる）。

         → 開いている間、**器の外の touchmove を止める**。body に position:fixed を当てる
         古典的なロックは sticky ヘッダーを壊すので使わない
         （§25 の裁定・[[knowledge-body-overflow-hidden-kills-sticky]]）。 */
      /* 背後の固定／解除。CSS では止まらなかった（`overflow: clip` はプログラム的な
         スクロールを通す＝実測で pageY 800 まで動いた）。**body を固定して位置を持ち替える**
         のが確実で、iOS でも定番の方法。`overflow: hidden` と違い sticky を壊さない
         （文書ごと動かないので、ヘッダーはその場に留まる）。 */
      this.lockScroll = () => {
        if (this.lockedY != null) return;
        this.lockedY = window.scrollY || 0;
        const b = document.body;
        b.style.position = 'fixed';
        b.style.top = `-${this.lockedY}px`;
        b.style.left = '0';
        b.style.right = '0';
        b.style.width = '100%';
      };

      this.unlockScroll = () => {
        if (this.lockedY == null) return;
        const y = this.lockedY;
        this.lockedY = null;
        const b = document.body;
        b.style.position = '';
        b.style.top = '';
        b.style.left = '';
        b.style.right = '';
        b.style.width = '';
        window.scrollTo(0, y);
      };

      this.armPrefetch();
      if (this.dialog) {
        /* §162-28（2026-08-08 user 実機報告で構造修正）
           モードを input のイベント聞き取りで溜め込むと、**純正部品がプログラムで入力欄を
           空にする経路**（閉じる時の resetSearch・クリアボタン）でイベントが出ず、
           「入力あり」のまま取り残される＝再び開いても軸が出ない。
           → 溜め込まない。**開閉のたびに input.value の実値から計算し直す**。 */
        const onToggle = () => {
          if (this.dialog.open) {
            this.prefetch();
            /* §179-14：body 固定式ロック（§162-38）は **PC の modal 経路だけ**。
               SP のヘッダー直下シート（§179）は非ロックの文法＝面がヘッダー下を覆い・
               上端は追従変数・器は overscroll contain で足りる。SP で掛けると、
               閉じる時の「固定解除 → 一瞬 TOP 描画 → scrollTo 復元」が背景に露出する
               （user 実機報告 2026-08-13＝閉じた直後に TOP→元位置のジャンプ） */
            if (!window.matchMedia('(max-width: 989px)').matches) this.lockScroll();
          } else {
            this.unlockScroll();
          }
          requestAnimationFrame(() => this.syncMode());
        };
        new MutationObserver(onToggle).observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
        this.dialog.addEventListener('close', () => {
          this.unlockScroll();
          /* §195：§16.23 の初期状態は全軸 closed。
             条件変更中の開閉記憶は面の中だけで使い、閉扉をまたいで持ち越さない。 */
          this._openAxes = new Set();
          this._hadAxes = true;
          this.axesEl?.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
          /* §179-15：時差入場の印を後始末（次の開扉で改めて 1 回だけ発火させる） */
          this._enterPending = false;
          window.clearTimeout(this._enterTimer);
          this.axesEl?.classList.remove('ig-enter-run');
          requestAnimationFrame(() => this.syncMode());
        });
        // 途中で離脱しても固定を残さない
        window.addEventListener('pagehide', () => this.unlockScroll());
        /* 純正が入力値をプログラムで書き換えるもう 1 つの経路＝クリアボタン（開いたまま空になる） */
        this.dialog.addEventListener('click', (e) => {
          if (e.target.closest('.predictive-search__reset-button')) {
            /* reset は描画差し替えの後に値を消す＝1 コマ後ではまだ旧値（実測）。値が空になるのを待って再計算 */
            const wait = (tries) => {
              if (!(this.input?.value || '').trim() || tries <= 0) return this.syncMode();
              setTimeout(() => wait(tries - 1), 100);
            };
            setTimeout(() => wait(10), 50);
          }
        });
        onToggle();
      }
    }

    hasSelection() {
      return this.selected.size > 0 || this.price.gte != null || this.price.lte != null;
    }
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }

  function init() {
    document.querySelectorAll(ROOT).forEach((el) => {
      if (el.__igSearchPanel) return;
      el.__igSearchPanel = new SearchPanel(el);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
